import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faUser } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";

// One search box that fans out to BOTH existing search endpoints in parallel:
//   • Accounts  → SEARCH_MATCHSEEKER_URL (name / email / mobile)
//   • Profiles  → SEARCH_BG_PROFILES_URL (name / caste / city / profession /
//                 income / sub-caste + owner email/name/phone, cross-collection)
// Account rows open the owner's Deep Dive; profile rows open the full profile.
// No new backend — reuses the verified endpoints, merges client-side.
const GlobalSearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const runSearch = async () => {
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setSearched(true);
    try {
      const params = { query: q, page: 1, limit: 15 };
      const [accRes, profRes] = await Promise.allSettled([
        func.get(urls.SEARCH_MATCHSEEKER_URL, { params }),
        func.get(urls.SEARCH_BG_PROFILES_URL, { params }),
      ]);
      setAccounts(
        accRes.status === "fulfilled"
          ? accRes.value?.data?.matchSeekers || accRes.value?.data?.data || []
          : []
      );
      setProfiles(
        profRes.status === "fulfilled"
          ? profRes.value?.data?.bgProfiles || profRes.value?.data?.data || []
          : []
      );
      if (accRes.status === "rejected" && profRes.status === "rejected") {
        toast.error("Search failed. Check your connection.");
      }
    } catch (e) {
      toast.error("Search failed.");
    } finally {
      setLoading(false);
    }
  };

  const initial = (s) => (s || "?").toString().trim().charAt(0).toUpperCase() || "?";
  const total = accounts.length + profiles.length;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <h1 className="text-xl font-bold text-slate-800 mb-1">Find anyone</h1>
      <p className="text-sm text-slate-500 mb-5">
        One search across accounts and profiles — by name, email, phone, caste, city, profession or income.
      </p>

      <div className="flex gap-2 mb-6">
        <div className="relative flex-1">
          <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            placeholder="Search name, email, phone, caste, city…"
            className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
          />
        </div>
        <button
          onClick={runSearch}
          disabled={loading || !query.trim()}
          className="px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition"
        >
          {loading ? "Searching…" : "Search"}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400 text-sm">Searching…</div>
      ) : !searched ? (
        <div className="text-center py-16 text-slate-300 text-sm">
          Type a name, phone, email, caste, city, profession or income and hit Search.
        </div>
      ) : total === 0 ? (
        <div className="text-center py-16">
          <FontAwesomeIcon icon={faSearch} className="text-slate-300 text-2xl mb-2" />
          <p className="text-slate-400 text-sm">No accounts or profiles match “{query.trim()}”.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {accounts.length > 0 && (
            <Section title="Accounts" count={accounts.length} badgeClass="bg-indigo-100 text-indigo-700">
              {accounts.map((a) => {
                const uid = a.userId?._id || a.userId;
                return (
                  <Row
                    key={a._id}
                    initial={initial(a.fullName)}
                    title={a.fullName || "—"}
                    sub={[a.emailId, a.phoneNumber].filter(Boolean).join("  ·  ") || "—"}
                    chip="Account"
                    chipClass="bg-indigo-100 text-indigo-700"
                    disabled={!uid}
                    onClick={() => uid && navigate(`/user-deep-dive/${uid}`)}
                  />
                );
              })}
            </Section>
          )}
          {profiles.length > 0 && (
            <Section title="Profiles" count={profiles.length} badgeClass="bg-emerald-100 text-emerald-700">
              {profiles.map((p) => (
                <Row
                  key={p._id}
                  initial={initial(p.firstName)}
                  title={[p.firstName, p.lastName].filter(Boolean).join(" ") || "—"}
                  sub={[p.caste?.caste, p.residentCity || p.birthCity].filter(Boolean).join("  ·  ") || "—"}
                  chip="Profile"
                  chipClass="bg-emerald-100 text-emerald-700"
                  onClick={() => navigate(`/user-management/bride-groom-profile/view/${p._id}`)}
                />
              ))}
            </Section>
          )}
        </div>
      )}
    </div>
  );
};

const Section = ({ title, count, badgeClass, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-2">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</h2>
      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${badgeClass}`}>{count}</span>
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const Row = ({ initial, title, sub, chip, chipClass, onClick, disabled }) => (
  <div
    onClick={disabled ? undefined : onClick}
    className={`flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-100 shadow-sm transition ${
      disabled ? "opacity-50" : "cursor-pointer hover:border-indigo-200 hover:shadow"
    }`}
  >
    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold text-sm flex-shrink-0">
      {initial && initial !== "?" ? initial : <FontAwesomeIcon icon={faUser} />}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-slate-700 truncate">{title}</p>
      <p className="text-xs text-slate-400 truncate">{sub}</p>
    </div>
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${chipClass}`}>{chip}</span>
    <span className="text-slate-300 text-lg leading-none">›</span>
  </div>
);

export default GlobalSearch;
